import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    OnInit,
    inject,
    signal,
} from '@angular/core'
import { ICON_NAMES, LEAD_DASHBOARD_QUERY_PARAMS } from '@app/core/constants/app.constants'
import { ELeadDashboardPipelineView } from '@app/core/enum/client.enum'
import { CRM_ROUTE_PATHS } from '@app/core/constants/client.constants'
import { PRODUCT_BLOCK } from '@app/features/school-site/constants/proposal.constants'
import { PermissionService } from '@core/services/permission-service/permission-service'
import { PERMISSIONS } from '@app/core/constants/permissions.constants'
import { ShikshaPermissionsService } from '@core/services/shiksha-permissions/shiksha-permissions.service'
import { SHIKSHA_FEATURES } from '@core/constants/shiksha-permissions.constants'
import { Router, RouterLink } from '@angular/router'
import { OMS_ROUTES } from '@app/core/constants/generate-proposal.constants'
import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component'
import { SALES_ASSIST_ROUTE_PATHS } from '@app/features/sales-assist/constants/sales-assist.constants'
import { UserStore } from '@core/services/user/user.store'
import { ReporteesService } from '@core/services/reportees/reportees.service'
import { canAccessEiBannerAdmin } from '@app/features/ei-banner/utils/banner-access'
import { firstValueFrom } from 'rxjs'

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, SvgIconComponent],
})
export class SidebarComponent implements OnInit {
    permissionService = inject(PermissionService)
    shikshaPermissionsService = inject(ShikshaPermissionsService)
    private readonly router = inject(Router)
    private readonly userStore = inject(UserStore)
    private readonly reporteesService = inject(ReporteesService)
    private readonly cdr = inject(ChangeDetectorRef)

    hasReportees = signal(false)

    @Input() isExpanded = true
    @Output() isExpandedChange = new EventEmitter<boolean>()
    public readonly ICONS = ICON_NAMES
    public readonly CRM_ROUTE_PATHS = CRM_ROUTE_PATHS
    public readonly PRODUCT_BLOCK = PRODUCT_BLOCK
    public readonly PERMISSIONS = PERMISSIONS
    public readonly SHIKSHA_FEATURES = SHIKSHA_FEATURES
    public readonly SALES_ASSIST_ROUTE_PATHS = SALES_ASSIST_ROUTE_PATHS
    /** Clears `user_id` when returning to a ZM pipeline view from an RM drill-down. */
    public readonly pipelineMyLeadsQuery: Record<string, string | null> = {
        [LEAD_DASHBOARD_QUERY_PARAMS.VIEW]: ELeadDashboardPipelineView.MY_LEADS,
        [LEAD_DASHBOARD_QUERY_PARAMS.USER_ID]: null,
    }
    public readonly pipelineTeamQuery: Record<string, string | null> = {
        [LEAD_DASHBOARD_QUERY_PARAMS.VIEW]: ELeadDashboardPipelineView.TEAM,
        [LEAD_DASHBOARD_QUERY_PARAMS.USER_ID]: null,
    }
    public readonly pipelineUnassignedQuery: Record<string, string | null> = {
        [LEAD_DASHBOARD_QUERY_PARAMS.VIEW]: ELeadDashboardPipelineView.UNASSIGNED,
        [LEAD_DASHBOARD_QUERY_PARAMS.USER_ID]: null,
    }
    public readonly omsProposalSummaryLink: readonly string[] = [
        '/',
        OMS_ROUTES.BASE,
        OMS_ROUTES.PROPOSAL_SUMMARY,
    ]
    public isManageWebsiteExpanded = false
    public isApprovalsNavExpanded = true
    public isFinanceNavExpanded = true
    public isMyWorkExpanded = true
    public isPipelineExpanded = true
    public isCustomerExpanded = true
    public isPerformanceExpanded = true
    public isMcpAnalyticsExpanded = true
    public isSettingsExpanded = true
    public isPreferencesExpanded = true

    async ngOnInit(): Promise<void> {
        const userId = this.userStore.getUserData()?.user_id
        if (!userId) {
            return
        }
        try {
            const hasReportees = await firstValueFrom(this.reporteesService.hasReportees(userId))
            this.hasReportees.set(hasReportees)
        } catch {
            this.hasReportees.set(false)
        }
        this.cdr.markForCheck()
    }

    /** Create Client submenu only when we have 2+ items (e.g. Single + Bulk Upload). */
    get hasCreateClientSubmenu(): boolean {
        return this.shikshaPermissionsService.isShikshaFeature(SHIKSHA_FEATURES.BULK_UPLOAD)
    }

    get showManageWebsite(): boolean {
        return (
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_BLOGS) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_TEACHER_RESOURCES) ===
                true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_CBE_RESOURCES) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_TEAMS) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_CAREERS) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_NEWS) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_WEBINARS) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_DOWNLOADS) === true ||
            this.permissionService.getPermissionByName(PERMISSIONS.MANAGE_NEWSLETTERS) === true
        )
    }

    /**
     * Payments (NAV-12643): the Finance queue. Review implies view so a Finance user
     * granted only review_payment_claims still sees the section.
     */
    get showFinanceNav(): boolean {
        return (
            this.permissionService.canAccessPermissionOrImplicitFullAccess(
                PERMISSIONS.VIEW_PAYMENT_CLAIMS,
            ) ||
            this.permissionService.canAccessPermissionOrImplicitFullAccess(
                PERMISSIONS.REVIEW_PAYMENT_CLAIMS,
            )
        )
    }

    /** CSL users with both client + proposal approval: dedicated Approvals nav with Client / Order (not under My Work). */
    get showCslDualApprovalNav(): boolean {
        return (
            !!this.permissionService.getPermissionByName(PERMISSIONS.APPROVE_CLIENT) &&
            !!this.permissionService.getPermissionByName(PERMISSIONS.APPROVE_PROPOSAL)
        )
    }

    get hasPipelineLinks(): boolean {
        return (
            !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_MY_LEAD) ||
            !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_TEAM_LEADS)
        )
    }

    get hasMyWorkLinks(): boolean {
        return (
            !this.showCslDualApprovalNav &&
            !!this.permissionService.getPermissionByName(PERMISSIONS.APPROVE_PROPOSAL)
        )
    }

    get hasPerformanceLinks(): boolean {
        return (
            !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_MY_RUC_DASHBOARD) ||
            !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_TEAM_RUC_DASHBOARD) ||
            !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_MY_REVENUE_DASHBOARD) ||
            !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_TEAM_REVENUE_DASHBOARD)
        )
    }

    get hasMcpAnalyticsLinks(): boolean {
        return !!this.permissionService.getPermissionByName(PERMISSIONS.VIEW_MCP_DASHBOARD)
    }

    /**
     * Default revenue route: prefer my dashboard when permitted (same idea as RUC landing on my view).
     */
    revenueDashboardPath(): string {
        if (this.permissionService.getPermissionByName(PERMISSIONS.VIEW_MY_REVENUE_DASHBOARD)) {
            return '/my-revenue'
        }
        if (this.permissionService.getPermissionByName(PERMISSIONS.VIEW_TEAM_REVENUE_DASHBOARD)) {
            return '/team-revenue'
        }
        return '/my-revenue'
    }

    get hasAcademicCalendarAccess(): boolean {
        return this.permissionService.canAccessPermissionOrImplicitFullAccess(
            PERMISSIONS.VIEW_ACADEMIC_CALENDAR,
        )
    }

    /** Mirrors `fieldVisitAccessGuard` — the link must not outlive the route's own gate. */
    get hasFieldVisitAccess(): boolean {
        return this.permissionService.canAccessPermissionOrImplicitFullAccess(
            PERMISSIONS.VIEW_FIELD_VISIT,
        )
    }

    /** Mirrors `bannerAccessGuard` — hide the link when the route would deny entry. */
    get canShowEiBanner(): boolean {
        return canAccessEiBannerAdmin(
            this.permissionService,
            this.userStore.getUserData()?.user_type,
        )
    }

    toggleSidebar(): void {
        this.isExpanded = !this.isExpanded
        this.isExpandedChange.emit(this.isExpanded)
    }

    menuItemClicked(): void {
        this.isExpanded = false
        this.isExpandedChange.emit(this.isExpanded)
    }

    onSidebarBackdropClick(event: Event): void {
        if (event.target === event.currentTarget) {
            this.toggleSidebar()
        }
    }

    onApprovalsNavClick(event: Event): void {
        event.preventDefault()
        this.menuItemClicked()
        this.router.navigate(this.omsProposalSummaryLink)
    }

    toggleManageWebsite(): void {
        this.isManageWebsiteExpanded = !this.isManageWebsiteExpanded
    }

    toggleFinanceNav(): void {
        this.isFinanceNavExpanded = !this.isFinanceNavExpanded
    }

    toggleApprovalsNav(): void {
        this.isApprovalsNavExpanded = !this.isApprovalsNavExpanded
    }

    toggleMyWork(): void {
        this.isMyWorkExpanded = !this.isMyWorkExpanded
    }

    togglePipeline(): void {
        this.isPipelineExpanded = !this.isPipelineExpanded
    }

    toggleCustomer(): void {
        this.isCustomerExpanded = !this.isCustomerExpanded
    }

    togglePerformance(): void {
        this.isPerformanceExpanded = !this.isPerformanceExpanded
    }

    toggleMcpAnalytics(): void {
        this.isMcpAnalyticsExpanded = !this.isMcpAnalyticsExpanded
    }

    toggleSettings(): void {
        this.isSettingsExpanded = !this.isSettingsExpanded
    }

    togglePreferences(): void {
        this.isPreferencesExpanded = !this.isPreferencesExpanded
    }
}
